import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X, Check, Shield, BarChart3, Megaphone } from "lucide-react";

const KEY = "prenotoo-cookie-consent";
const VERSION = 3;

/** GDPR-compliant cookie consent banner (Italian).
 *
 *  Persists granular categories in localStorage:
 *   { v: 3, at: ISO, necessary: true, analytics: false, marketing: false }
 *
 *  - "necessary" is always true (technical cookies, art. 122 Codice Privacy)
 *  - "analytics" / "marketing" default OFF; require explicit opt-in
 *  - Re-prompts when VERSION changes
 *  - Hidden inside `?embed=1` iframe widget context
 *
 *  The platform itself uses ZERO analytics/marketing cookies — the toggles
 *  exist for UX clarity ("you can choose") and to stay forward-compatible if
 *  optional tools are added later.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });
  const location = useLocation();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) { setVisible(true); return; }
      const v = JSON.parse(raw);
      if (!v || v.v !== VERSION) {
        setVisible(true);
        return;
      }
      setPrefs({ necessary: true, analytics: !!v.analytics, marketing: !!v.marketing });
      setVisible(false);
    } catch {
      setVisible(true);
    }
  }, []);

  // Hide inside widget embed iframe
  if (new URLSearchParams(location.search).get("embed") === "1") return null;
  if (!visible) return null;

  function persist(p) {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        v: VERSION,
        at: new Date().toISOString(),
        necessary: true,
        analytics: !!p.analytics,
        marketing: !!p.marketing,
      }));
    } catch { /* noop */ }
    setPrefs({ necessary: true, ...p });
    setVisible(false);
    // Dispatch custom event so other parts of the app can react if needed
    try { window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: p })); } catch { /* noop */ }
  }

  function acceptAll() { persist({ analytics: true, marketing: true }); }
  function rejectAll() { persist({ analytics: false, marketing: false }); }
  function saveCustom() { persist({ analytics: prefs.analytics, marketing: prefs.marketing }); }

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[60] bg-zinc-900 text-white rounded-2xl shadow-2xl p-5"
      role="dialog"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
          <Cookie className="h-4 w-4" />
        </div>
        <div className="flex-1 text-sm leading-relaxed">
          <div id="cookie-banner-title" className="font-semibold mb-1">
            {showCustomize ? "Personalizza i cookie" : "Cookie e privacy"}
          </div>
          {!showCustomize ? (
            <div className="text-zinc-300 text-xs">
              Prenotoo utilizza solo cookie tecnici di sessione.
              Nessun cookie di profilazione, marketing o analytics di terze parti.
              {" "}
              <Link to="/cookie-policy" className="underline hover:text-white" data-testid="cookie-banner-link">
                Cookie Policy
              </Link>
              {" · "}
              <Link to="/privacy" className="underline hover:text-white">Privacy</Link>
            </div>
          ) : (
            <div className="space-y-2 mt-1" data-testid="cookie-banner-customize">
              <CategoryRow
                icon={Shield}
                title="Necessari"
                desc="Indispensabili per autenticazione e funzionamento. Sempre attivi."
                checked
                disabled
                testId="cookie-cat-necessary"
              />
              <CategoryRow
                icon={BarChart3}
                title="Analytics"
                desc="Statistiche aggregate. Attualmente non utilizzati."
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                testId="cookie-cat-analytics"
              />
              <CategoryRow
                icon={Megaphone}
                title="Marketing"
                desc="Profilazione e remarketing. Attualmente non utilizzati."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testId="cookie-cat-marketing"
              />
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        {!showCustomize ? (
          <>
            <button
              onClick={() => setShowCustomize(true)}
              className="text-xs px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 text-white transition"
              data-testid="cookie-banner-customize-btn"
            >
              Personalizza
            </button>
            <button
              onClick={rejectAll}
              className="text-xs px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 text-white transition"
              data-testid="cookie-banner-reject"
            >
              Rifiuta
            </button>
            <button
              onClick={acceptAll}
              className="text-xs px-4 py-2 rounded-full bg-white text-zinc-900 hover:bg-zinc-100 font-medium transition inline-flex items-center gap-1.5 dark:text-zinc-200"
              data-testid="cookie-banner-accept"
            >
              <Check className="h-3.5 w-3.5" /> Accetta tutti
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setShowCustomize(false)}
              className="text-xs px-4 py-2 rounded-full bg-transparent hover:bg-white/10 text-white transition inline-flex items-center gap-1"
              data-testid="cookie-banner-back"
            >
              <X className="h-3.5 w-3.5" /> Annulla
            </button>
            <button
              onClick={saveCustom}
              className="text-xs px-4 py-2 rounded-full bg-white text-zinc-900 hover:bg-zinc-100 font-medium transition inline-flex items-center gap-1.5 dark:text-zinc-200"
              data-testid="cookie-banner-save"
            >
              <Check className="h-3.5 w-3.5" /> Salva preferenze
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function CategoryRow({ icon: Icon, title, desc, checked, disabled, onChange, testId }) {
  return (
    <label className={`flex items-start gap-2 p-2 rounded-lg ${disabled ? "opacity-70" : "hover:bg-white/5 cursor-pointer"}`} data-testid={testId}>
      <Icon className="h-4 w-4 mt-0.5 text-zinc-300 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold flex items-center gap-2">
          {title}
          {disabled && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">Attivi</span>}
        </div>
        <div className="text-[10.5px] text-zinc-400 leading-snug">{desc}</div>
      </div>
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500 mt-0.5"
        data-testid={`${testId}-toggle`}
      />
    </label>
  );
}

/**
 * Reset cookie preferences (used by a "Cookie" link in the footer to re-open
 * the banner). Exported for use elsewhere.
 */
export function resetCookieConsent() {
  try {
    localStorage.removeItem(KEY);
    window.location.reload();
  } catch { /* noop */ }
}
